import { useCompaniesInvolved } from 'hooks/useCompanies';
import { useProjectActivitySummary } from 'hooks/useProjectActivitySummary';
import { useProjectEvents } from 'hooks/useProjectEvents';
import { useProjectLandTitleValue } from 'hooks/useProjectLandTitleValue';
import { useProject } from 'hooks/useProjects';
import { useProjectsByAddress } from 'hooks/useProjectsByAddress';
import { useProjectSizeInfo } from 'hooks/useProjectSizeInfo';
import { useProjectWorkType } from 'hooks/useProjectWorkType';

/**
 * Custom hook to fetch and manage project details data.
 *
 * This hook aggregates data from multiple sources related to a project,
 * including project information, activity summary, involved companies,
 * events, size info, and work type. It also provides loading states for
 * each data fetch operation.
 */
export const useProjectDetails = (projectSlug: string) => {
  const {
    data: project,
    isLoading: isLoadingProject,
    error: projectError,
  } = useProject(projectSlug!);
  const {
    isLoading: isLoadingProjectActivitySummary,
    data: projectActivitySummary,
    isError: isErrorLoadingActivitySummary,
  } = useProjectActivitySummary(projectSlug || '');
  const { data: involvedCompanies, isLoading: isCompaniesInvolvedLoading } =
    useCompaniesInvolved(projectSlug);
  const { isLoading: isLoadingProjectEvents, data: projectEvents } = useProjectEvents(
    projectSlug!,
  );
  const {
    projects: projectsList,
    isLoading: isProjectsByAddressLoading,
    count: numberOfProjectsByAddress,
    setPage,
  } = useProjectsByAddress({});
  const { data: projectSizeInfo } = useProjectSizeInfo(projectSlug!);
  const { data: projectWorkType } = useProjectWorkType(projectSlug!);
  const { data: landTitleHistory, isLoading: isLoadingLandTitleHistory } =
    useProjectLandTitleValue(projectSlug!);

  return {
    project,
    isLoadingProject,
    projectActivitySummary,
    isLoadingProjectActivitySummary,
    isErrorLoadingActivitySummary,
    involvedCompanies,
    isCompaniesInvolvedLoading,
    projectEvents,
    isLoadingProjectEvents,
    projectSizeInfo,
    projectWorkType,
    projectsList,
    isProjectsByAddressLoading,
    numberOfProjectsByAddress,
    setPage,
    landTitleHistory,
    isLoadingLandTitleHistory,
    projectError,
  };
};
